// Everything about one piece of leave, plus the buttons to approve, decline or cancel it if you're allowed to.

import { useEffect, useState } from 'react';
import { fmtLong, fmtRange, formatDays, leaveYearOf } from '../lib/dates';
import { balanceFor, canDecide, displayType, partSuffix, requestDays, typeOf } from '../lib/leave';
import { useStore } from '../store/StoreProvider';
import { useCtx } from '../store/useCtx';
import { EmployeeName, StatusChip } from '../ui/bits';
import { useDialogs } from '../ui/dialogs';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { useRun } from '../ui/Toasts';

export function RequestDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const ctx = useCtx();
  const store = useStore();
  const run = useRun();
  const { confirm } = useDialogs();
  const [reply, setReply] = useState('');

  const req = ctx.requests.find((r) => r.id === id);
  useEffect(() => {
    if (!req) onClose(); // it was removed while the dialog was open
  }, [req, onClose]);
  if (!req) return null;

  const owner = ctx.userById.get(req.userId);
  const type = displayType(req, ctx.me, ctx);
  const isOwn = req.userId === ctx.me.id;
  const decide = canDecide(ctx.me, req, ctx);
  const decider = req.decidedBy ? ctx.userById.get(req.decidedBy) : null;
  const days = requestDays(req, ctx.bh);
  const active = req.status === 'pending' || req.status === 'approved';
  const canCancel = active && (isOwn || decide);
  const dept = owner?.departmentId ? ctx.deptById.get(owner.departmentId) : undefined;
  const showNote = isOwn || decide || req.typeId !== 'sick';
  const part = partSuffix(req);
  const deciding = decide && req.status === 'pending';

  let balanceLine = '';
  if (deciding && owner && typeOf(req.typeId).deducts) {
    const b = balanceFor(owner, leaveYearOf(req.start, ctx.settings.leaveYearStartMonth), ctx);
    balanceLine = `${owner.name} has ${formatDays(b.remaining)} day${b.remaining === 1 ? '' : 's'} left including this request.`;
  }

  const decideWith = async (status: 'approved' | 'declined') => {
    if (await run(store.decideRequest(id, status, reply.trim()), status === 'approved' ? 'Approved.' : 'Declined.')) onClose();
  };

  const cancel = async () => {
    const ok = await confirm({
      title: 'Cancel this leave?',
      message: `${fmtRange(req.start, req.end)} will be removed from the calendar${isOwn ? '' : ` and ${owner?.name} will no longer be off`}.`,
      confirmLabel: 'Cancel leave',
      danger: true,
    });
    if (ok && (await run(store.cancelRequest(id), 'Leave cancelled.'))) onClose();
  };

  return (
    <Modal title={<span className="employee-identity"><EmployeeName user={owner} /><span>· {type.label}</span></span>} titleId="detail-title" onClose={onClose}>
      <dl className="detail-list">
        <div>
          <dt>Dates</dt>
          <dd>
            {fmtRange(req.start, req.end)}
            {part && <span className="muted"> ({part})</span>}
          </dd>
        </div>
        <div><dt>From</dt><dd>{fmtLong(req.start)}</dd></div>
        <div><dt>To</dt><dd>{fmtLong(req.end)}</dd></div>
        <div><dt>Working days</dt><dd>{formatDays(days)}</dd></div>
        <div><dt>Status</dt><dd><StatusChip status={req.status} /></dd></div>
        {dept && <div><dt>Department</dt><dd>{dept.name}</dd></div>}
        {showNote && req.note && <div><dt>Note</dt><dd className="wrap">{req.note}</dd></div>}
        {decider && req.status !== 'pending' && (
          <div><dt>{req.status === 'cancelled' ? 'Cancelled by' : 'Decided by'}</dt><dd><EmployeeName user={decider} /></dd></div>
        )}
        {req.decisionNote && <div><dt>Reply</dt><dd className="wrap">{req.decisionNote}</dd></div>}
      </dl>

      {balanceLine && (
        <p className="summary-line"><Icon name="info" size={16} /><span>{balanceLine}</span></p>
      )}

      {deciding && (
        <div className="field">
          <label htmlFor="d-note">Reply <span className="optional">(optional, visible to {isOwn ? 'you' : owner?.name})</span></label>
          <textarea id="d-note" rows={2} maxLength={300} value={reply} onChange={(e) => setReply(e.target.value)} />
        </div>
      )}

      <div className="dialog-actions">
        {canCancel && (
          <button type="button" className="btn btn-ghost-danger" onClick={cancel}>
            <Icon name="trash" size={16} />
            {isOwn ? 'Cancel this leave' : 'Cancel leave'}
          </button>
        )}
        <span className="spacer" />
        {deciding ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => decideWith('declined')}>Decline</button>
            <button type="button" className="btn" onClick={() => decideWith('approved')}>
              <Icon name="check" size={16} />
              Approve
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        )}
      </div>
    </Modal>
  );
}
